import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, setActiveTownId } from "../api";
import { charSprite } from "../game/sprites";
import { useEffect, useRef } from "react";
import { useSession } from "../stores/session";

const COLORS = ["#f59e0b", "#65a30d", "#0284c7", "#e11d48", "#c084fc", "#facc15", "#0d9488", "#78716c"];
const HATS = [
  { id: "none", name: "无" },
  { id: "straw", name: "草帽" },
  { id: "beret", name: "贝雷帽" },
  { id: "cap", name: "棒球帽" },
  { id: "wizard", name: "尖顶帽" },
  { id: "flower", name: "花环" },
  { id: "band", name: "头巾" },
  { id: "glasses", name: "眼镜" },
];
const PRESETS = [
  { personality: "开朗热心,喜欢交朋友,嘴上总挂着笑", wish: "开一家全镇最温暖的小店" },
  { personality: "安静内向,喜欢观察,内心戏丰富", wish: "写一本关于小镇居民的故事集" },
  { personality: "冒险精神旺盛,坐不住,总爱尝试新鲜事物", wish: "画出一幅小镇的全景地图" },
  { personality: "吃货一枚,人生信条是唯美食不可辜负", wish: "尝遍小镇每家店的招牌菜" },
];

export default function CreateAgent() {
  const [name, setName] = useState("");
  const [gender, setGender] = useState<"male" | "female">("male");
  const [genderCounts, setGenderCounts] = useState<{ male: number; female: number } | null>(null);
  const [personality, setPersonality] = useState("");
  const [wish, setWish] = useState("");
  const [color, setColor] = useState(COLORS[0]);
  const [hat, setHat] = useState("none");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useSession();
  const townId = searchParams.get("townId") || user?.homeTownId || "";
  const previewRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (townId) setActiveTownId(townId);
  }, [townId]);

  useEffect(() => {
    const c = previewRef.current!;
    const g = c.getContext("2d")!;
    g.clearRect(0, 0, c.width, c.height);
    g.imageSmoothingEnabled = false;
    g.drawImage(charSprite(color, hat, 0), 0, 0, 32, 36, 16, 4, 96, 108);
  }, [color, hat]);

  // 性别默认值:按全镇男女比例取较少的一方;平衡则随机
  useEffect(() => {
    api.genderOptions().then((r) => {
      setGenderCounts(r.counts);
      setGender(r.suggest ?? (Math.random() < 0.5 ? "male" : "female"));
    }).catch(() => {});
  }, []);

  const submit = async () => {
    setError("");
    setLoading(true);
    try {
      await api.createAgent({ name, personality, wish, color, hat, gender });
      nav(townId ? `/town/${townId}` : "/town");
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-full overflow-y-auto flex items-center justify-center px-6 py-10"
      style={{ background: "linear-gradient(180deg, #10173a 0%, #1b2a5e 100%)" }}>
      <div className="w-full max-w-lg rounded-2xl p-7" style={{ background: "#F8F7E5", boxShadow: "0 20px 60px rgba(0,0,0,0.4)" }}>
        <h1 className="text-2xl font-black text-center text-[#1D1D1D]" style={{ fontFamily: '"ZCOOL KuaiLe","PingFang SC",sans-serif' }}>
          ⭐ 许下心愿,创造你的小生命
        </h1>
        <p className="text-center text-[#8a8571] text-sm mt-1 mb-6">它会带着你的心愿,在小镇里认真生活</p>

        <div className="flex justify-center mb-5">
          <div className="rounded-2xl p-3" style={{ background: "#e4f0d0", border: "2px dashed #b39c4f" }}>
            <canvas ref={previewRef} width={128} height={116} />
          </div>
        </div>

        <label className="block text-sm font-bold text-[#1D1D1D] mb-1">名字</label>
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={12} placeholder="给它起个名字"
          className="w-full mb-4 px-4 py-2.5 rounded-xl border border-[#ddd5b8] bg-white outline-none focus:border-[#b39c4f]" />

        <label className="block text-sm font-bold text-[#1D1D1D] mb-1">性别</label>
        <div className="flex gap-2 mb-1">
          {([["male", "♂ 男孩"], ["female", "♀ 女孩"]] as const).map(([g, label]) => (
            <button key={g} type="button" onClick={() => setGender(g)}
              className={`flex-1 px-3 py-2 rounded-xl border text-sm font-bold transition-colors ${gender === g
                ? (g === "male" ? "border-[#0284c7] bg-[#e0f2fe] text-[#0369a1]" : "border-[#db2777] bg-[#fce7f3] text-[#be185d]")
                : "border-[#ddd5b8] bg-white text-[#6b6252]"}`}>
              {label}
            </button>
          ))}
        </div>
        {genderCounts && (
          <p className="text-xs text-[#8a8571] mb-4">小镇当前性别分布:♂ {genderCounts.male} / ♀ {genderCounts.female};性别创建后不可修改
            {gender === "male" && genderCounts.female > genderCounts.male ? "(建议选 ♀,不过你可以自由选择)" : ""}
            {gender === "female" && genderCounts.male > genderCounts.female ? "(建议选 ♂,不过你可以自由选择)" : ""}
          </p>
        )}
        {!genderCounts && (
          <p className="text-xs text-[#8a8571] mb-4">性别创建后不可修改</p>
        )}

        <label className="block text-sm font-bold text-[#1D1D1D] mb-1">性格</label>
        <textarea value={personality} onChange={(e) => setPersonality(e.target.value)} maxLength={100} rows={2}
          placeholder="它是怎样一个小生命?"
          className="w-full mb-2 px-4 py-2.5 rounded-xl border border-[#ddd5b8] bg-white outline-none focus:border-[#b39c4f] resize-none" />

        <label className="block text-sm font-bold text-[#1D1D1D] mb-1">心愿</label>
        <textarea value={wish} onChange={(e) => setWish(e.target.value)} maxLength={100} rows={2}
          placeholder="它最想实现的事是什么?"
          className="w-full mb-2 px-4 py-2.5 rounded-xl border border-[#ddd5b8] bg-white outline-none focus:border-[#b39c4f] resize-none" />

        <div className="flex flex-wrap gap-2 mb-4">
          {PRESETS.map((p, i) => (
            <button key={i} onClick={() => { setPersonality(p.personality); setWish(p.wish); }}
              className="text-xs px-3 py-1.5 rounded-full border border-[#ddd5b8] text-[#6b6252] hover:border-[#b39c4f] hover:text-[#1D1D1D]">
              {p.wish}
            </button>
          ))}
        </div>

        <label className="block text-sm font-bold text-[#1D1D1D] mb-2">颜色</label>
        <div className="flex gap-2 mb-4">
          {COLORS.map((c) => (
            <button key={c} onClick={() => setColor(c)}
              className={`w-8 h-8 rounded-full transition-transform ${color === c ? "scale-125 ring-2 ring-offset-2 ring-[#1D1D1D]" : ""}`}
              style={{ background: c }} />
          ))}
        </div>

        <label className="block text-sm font-bold text-[#1D1D1D] mb-2">装扮</label>
        <div className="flex flex-wrap gap-2 mb-5">
          {HATS.map((h) => (
            <button key={h.id} onClick={() => setHat(h.id)}
              className={`text-xs px-3 py-1.5 rounded-full border ${hat === h.id ? "border-[#1D1D1D] bg-[#e8c96a] font-bold" : "border-[#ddd5b8] text-[#6b6252]"}`}>
              {h.name}
            </button>
          ))}
        </div>

        {error && <div className="mb-3 text-sm text-red-600">{error}</div>}
        <button onClick={submit} disabled={loading || name.length < 1 || personality.length < 2 || wish.length < 2}
          className="w-full py-3 rounded-xl font-bold text-[#1D1D1D] disabled:opacity-50 transition-transform hover:scale-[1.02]"
          style={{ background: "#e8c96a", boxShadow: "0 4px 0 #b39c4f" }}>
          {loading ? "星星正在落地……" : "✨ 让心愿落地"}
        </button>
      </div>
    </div>
  );
}
